import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { rbac } from '@dha-nexus/shared';
import { useApi } from '../../lib/useApi.js';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { useUrlPagination } from '../../lib/usePagination.js';
import {
  PageHeader,
  LoadingSkeleton,
  BrandedEmptyState,
  PageViewerControls,
  StaggerReveal,
  citationRoute,
} from '../../components/index.js';
import './ActivityPage.css';

// PRIME-ACT-UX-001 — personal activity history page.
//
// Replaces the retired right-side Activity rail with a normal Me-section page,
// patterned on NotificationsPage. The data is the signed-in user's OWN audited
// events, served server-scoped by GET /me/activity (the actor is enforced on
// the server; a spoofed actorUserId can never widen it). Admin/investigator
// reporting stays entirely separate at /admin/activity and /admin/audit.

// Plain-language phrasing for audit action codes. The raw code is always kept
// as secondary text, so unmapped codes still degrade gracefully to a humanized
// entity + verb. Curated overrides win for the codes with a natural phrasing.
const ACTION_LABEL_OVERRIDES = Object.freeze({
  'shell.operator_mode_enabled': 'Entered operator mode',
  'shell.operator_mode_disabled': 'Exited operator mode',
  'user.profile.update': 'Updated your profile',
  'user.preferences.update': 'Updated your preferences',
  'notification.mark_read': 'Marked a notification as read',
  'notification.mark_unread': 'Marked a notification as unread',
  'notification.dismiss': 'Dismissed a notification',
  'notification.acknowledge': 'Acknowledged a notification',
  'auth.login': 'Signed in',
  'auth.logout': 'Signed out',
});

const VERB_PHRASES = Object.freeze({
  create: 'Created',
  created: 'Created',
  update: 'Updated',
  updated: 'Updated',
  delete: 'Deleted',
  deleted: 'Deleted',
  add: 'Added',
  remove: 'Removed',
  submit: 'Submitted',
  submitted: 'Submitted',
  approve: 'Approved',
  approved: 'Approved',
  reject: 'Rejected',
  rejected: 'Rejected',
  enable: 'Enabled',
  enabled: 'Enabled',
  disable: 'Disabled',
  disabled: 'Disabled',
  view: 'Viewed',
  viewed: 'Viewed',
  export: 'Exported',
  generate: 'Generated',
  send: 'Sent',
  assign: 'Assigned',
  complete: 'Completed',
  close: 'Closed',
  open: 'Opened',
  start: 'Started',
  cancel: 'Cancelled',
});

const ENTITY_NOUNS = Object.freeze({
  project: 'project',
  portfolio: 'portfolio',
  program: 'program',
  gate: 'gate review',
  notification: 'notification',
  user: 'account',
  shell: 'workspace',
  feedback_package: 'feedback package',
  briefing: 'briefing',
  action: 'action item',
  meeting: 'meeting',
  work_item: 'work item',
  timesheet: 'timesheet',
  risk: 'risk',
  issue: 'issue',
  change: 'change request',
  ai_run: 'AI run',
  connector: 'connector',
});

function humanizeToken(token) {
  return String(token || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

export function plainActionLabel(action) {
  if (!action) return 'Activity';
  if (ACTION_LABEL_OVERRIDES[action]) return ACTION_LABEL_OVERRIDES[action];
  const parts = String(action).split('.');
  const verb = parts[parts.length - 1];
  const entity = parts.length > 1 ? parts[0] : '';
  const verbPhrase = VERB_PHRASES[verb] || humanizeToken(verb);
  const entityNoun = entity ? ENTITY_NOUNS[entity] || humanizeToken(entity).toLowerCase() : '';
  return entityNoun ? `${verbPhrase} ${entityNoun}` : verbPhrase;
}

// Deep-link resolution. The destination path reuses the shared citation route
// resolver (the same CITATION_ROUTE_TABLE the inspectors use) rather than a new
// mapping; a small id-prefix -> permission-key table gates each link with
// rbac.canSeeRoute so unauthorized targets render inert (AC-04, AC-05).
const PERMISSION_KEY_BY_ID_PREFIX = Object.freeze([
  ['ART-', '/knowledge'],
  ['KNW-', '/knowledge'],
  ['KB-', '/knowledge'],
  ['LL-', '/knowledge'],
  ['AIRUN-', '/ai/orchestration'],
  ['BRF-', '/products'],
  ['PF-', '/portfolios'],
  ['PGM-', '/programs'],
  ['PRJ-', '/projects'],
  ['INT-', '/projects'],
  ['FIN-', '/financials'],
  ['FUND-', '/financials/funding-sources'],
  ['RSK-', '/governance/risks'],
  ['ISS-', '/governance/issues'],
  ['CHG-', '/governance/changes'],
  ['ACT-', '/actions'],
  ['WI-', '/work-items/:id'],
  ['MTG-', '/meetings'],
  ['ALLOC-', '/resources/allocations/:id'],
  ['LOE-', '/analytics/loes'],
]);

export function resolveActivityLink(targetId, roles) {
  const to = citationRoute(targetId);
  if (!to) return null;
  const entry = PERMISSION_KEY_BY_ID_PREFIX.find(([prefix]) => String(targetId).startsWith(prefix));
  const permissionKey = entry?.[1];
  if (!permissionKey) return null;
  return rbac.canSeeRoute(permissionKey, roles) ? to : null;
}

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = (Date.now() - then) / 1000;
  if (diff < 60) return `${Math.max(1, Math.round(diff))}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

function absoluteTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

// Facet options are derived truthfully from the currently-loaded rows (plus the
// active selection so it never vanishes from the dropdown). We do NOT fabricate
// a cross-actor "relevant updates" feed — the data model only supports
// actor-scoped events cheaply (documented interpretation of ticket req 4).
function facetOptions(rows, field, selected) {
  const values = new Set(rows.map((r) => r[field]).filter(Boolean));
  if (selected) values.add(selected);
  return [...values].sort();
}

export function ActivityPage() {
  const { roles = [] } = useCurrentUser();
  const [params, setParams] = useSearchParams();
  const pager = useUrlPagination(params, setParams, { defaultPageSize: 10 });

  const action = params.get('action') || '';
  const targetType = params.get('targetType') || '';

  const query = { limit: pager.pageSize, offset: pager.offset };
  if (action) query.action = action;
  if (targetType) query.targetType = targetType;

  const r = useApi('/me/activity', { query });
  const rows = useMemo(() => r.data?.data || [], [r.data]);

  const actionFacets = useMemo(() => facetOptions(rows, 'action', action), [rows, action]);
  const typeFacets = useMemo(() => facetOptions(rows, 'targetType', targetType), [rows, targetType]);

  function setFilter(key, value) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    // Any filter change returns to the first page of results.
    next.delete('offset');
    setParams(next, { replace: true });
  }

  const filters = (
    <div className="activity-filters">
      <label className="activity-filter">
        <span className="eyebrow">Action</span>
        <select
          aria-label="Filter activity by action"
          value={action}
          onChange={(e) => setFilter('action', e.target.value)}
        >
          <option value="">All actions</option>
          {actionFacets.map((value) => (
            <option key={value} value={value}>
              {plainActionLabel(value)}
            </option>
          ))}
        </select>
      </label>
      <label className="activity-filter">
        <span className="eyebrow">Type</span>
        <select
          aria-label="Filter activity by target type"
          value={targetType}
          onChange={(e) => setFilter('targetType', e.target.value)}
        >
          <option value="">All types</option>
          {typeFacets.map((value) => (
            <option key={value} value={value}>
              {ENTITY_NOUNS[value] || humanizeToken(value)}
            </option>
          ))}
        </select>
      </label>
    </div>
  );

  if (r.loading && !r.data) {
    return (
      <>
        <PageHeader eyebrow="Me · Activity" title="My Activity" />
        <LoadingSkeleton rows={6} />
      </>
    );
  }

  const header = (
    <PageHeader
      eyebrow="Me · Activity"
      title="My Activity"
      subtitle="A record of the actions you have taken across DHA PRIME."
      actions={filters}
    />
  );

  if (r.error) {
    return (
      <>
        {header}
        <BrandedEmptyState
          icon="!"
          eyebrow="Could not load activity"
          title="Your activity is unavailable right now"
          description={
            r.error?.message ||
            'Something went wrong loading your activity history. Try again in a moment.'
          }
          tone="brick"
        />
      </>
    );
  }

  if (rows.length === 0) {
    const filtered = Boolean(action || targetType);
    return (
      <>
        {header}
        <BrandedEmptyState
          icon="✓"
          eyebrow={filtered ? 'No matches' : 'Nothing yet'}
          title={filtered ? 'No activity matches these filters' : 'No activity yet'}
          description={
            filtered
              ? 'Try clearing the action or type filter to see your full history.'
              : 'As you work in PRIME, the actions you take will show up here.'
          }
          tone="sage"
        />
      </>
    );
  }

  return (
    <>
      {header}
      <StaggerReveal
        className="activity-page-list"
        as="ul"
        aria-label="Your recent activity"
        data-testid="activity-page-list"
      >
        {rows.map((entry) => {
          const link = resolveActivityLink(entry.targetId, roles);
          const label = plainActionLabel(entry.action);
          return (
            <li key={entry.id} className="activity-page-row card" data-testid="activity-page-row">
              <div className="activity-page-row__main">
                <span className="activity-page-action">{label}</span>
                <code className="activity-page-code muted small">{entry.action}</code>
              </div>
              <div className="activity-page-row__meta">
                {entry.targetId ? (
                  <span className="activity-page-target">
                    {link ? (
                      <Link to={link} data-testid="activity-target-link">
                        {entry.targetType ? `${humanizeToken(entry.targetType)} · ` : ''}
                        {entry.targetId}
                      </Link>
                    ) : (
                      <span data-testid="activity-target-inert" className="muted">
                        {entry.targetType ? `${humanizeToken(entry.targetType)} · ` : ''}
                        {entry.targetId}
                      </span>
                    )}
                  </span>
                ) : null}
                <time
                  className="activity-page-time muted small"
                  dateTime={entry.occurredAt || ''}
                  title={absoluteTime(entry.occurredAt)}
                >
                  {absoluteTime(entry.occurredAt)} · {relativeTime(entry.occurredAt)}
                </time>
              </div>
              {entry.summary ? <p className="activity-page-summary">{entry.summary}</p> : null}
            </li>
          );
        })}
      </StaggerReveal>
      <PageViewerControls
        page={r.data?.page}
        label="activity records"
        pageSize={pager.pageSize}
        onOffsetChange={pager.setOffset}
        onPageSizeChange={pager.setPageSize}
      />
    </>
  );
}
